import { type ReactNode } from "react";

import clsx from "clsx";

import type { Partner } from "@north-beam/nb-client-side";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { Checkbox } from "@shared/checkbox";

export interface PartnerCveCheckboxProps {
  partner: Partner;
  partnerDisplayName: string;
  hasConsent: boolean;
  hasConnectedAccounts: boolean;
  canToggleCveConsent: boolean;
  onChangeConsent(partner: Partner, updatedConsent: boolean): void;
  warningMessage?: ReactNode;
}

export const PartnerCveCheckbox = ({
  partner,
  partnerDisplayName,
  hasConsent,
  hasConnectedAccounts,
  canToggleCveConsent,
  onChangeConsent,
  warningMessage,
}: PartnerCveCheckboxProps) => {
  const { user } = useUser();
  const isDisabled =
    !canToggleCveConsent || (!hasConsent && !hasConnectedAccounts);

  return (
    <LearnMoreTooltip
      key={partner}
      placement="top-start"
      title={`Missing connection for ${partnerDisplayName}`}
      content={`Before enabling Clicks + Deterministic Views Model to use ${partnerDisplayName} impression data, you should connect an account for it.`}
      linkLabel="Connect account"
      link={user.pathFromRoot("/settings/connections")}
      noInfoCircle
      showTooltip={canToggleCveConsent && !hasConsent && !hasConnectedAccounts}
    >
      <Checkbox
        value={hasConsent}
        label={
          <>
            {partnerDisplayName}
            {warningMessage && (
              <GenericTooltip noInfoCircle content={warningMessage}>
                <i className="fa-regular fa-triangle-exclamation text-sun+2" />
              </GenericTooltip>
            )}
          </>
        }
        labelClassName={clsx("flex items-center gap-2", {
          "opacity-70": isDisabled,
        })}
        isDisabled={isDisabled}
        onChange={(updatedConsent: boolean) =>
          onChangeConsent(partner, updatedConsent)
        }
      />
    </LearnMoreTooltip>
  );
};
